import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../firebase";
import { collection, addDoc, doc, getDoc, serverTimestamp } from "firebase/firestore";

// Öffentlicher, fester Pfad des Anmeldeformulars (kein Login nötig) – wird auch im
// Adminbereich (Ferienlager.js) angezeigt, damit der Link leicht kopiert/geteilt werden kann.
// Optionaler Query-Parameter ?lager=<id> legt fest, zu welchem Lager die Anmeldung gehört;
// ohne Angabe wird das vom Admin als "aktuell" markierte Lager verwendet (settings/ferienlagerAktuell).
export const REGISTRIERUNG_URL = "/ferienlager-anmeldung";

const leer = {
  childFirstName: "", childLastName: "", childGender: "", childBirthdate: "", tshirtSize: "",
  guardianName: "", street: "", zip: "", city: "", phone: "", email: "",
  groupWishes: "", notes: "", website: "", // website = Honeypot
};

export default function FerienlagerAnmeldung() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(leer);
  const [gesendet, setGesendet] = useState(false);
  const [fehler, setFehler] = useState("");
  const [wirdGesendet, setWirdGesendet] = useState(false);
  const [lagerId, setLagerId] = useState(undefined); // undefined = wird ermittelt, null = keins gefunden
  const [lagerName, setLagerName] = useState("");

  useEffect(() => {
    const ausUrl = searchParams.get("lager");
    if (ausUrl) { setLagerId(ausUrl); return; }
    getDoc(doc(db, "settings", "ferienlagerAktuell")).then((snap) => {
      if (snap.exists()) { setLagerId(snap.data().lagerId || null); setLagerName(snap.data().lagerName || ""); }
      else setLagerId(null);
    }).catch(() => setLagerId(null));
  }, [searchParams]);

  const set = (feld) => (e) => setData({ ...data, [feld]: e.target.value });

  const absenden = async (e) => {
    e.preventDefault();
    setFehler("");
    if (data.website) return; // Honeypot ausgelöst -> stiller Abbruch

    if (!data.childFirstName || !data.childLastName || !data.childGender || !data.childBirthdate ||
        !data.guardianName || !data.street || !data.zip || !data.city || !data.phone || !data.email) {
      setFehler("Bitte füllt alle Pflichtfelder (*) aus.");
      return;
    }

    setWirdGesendet(true);
    try {
      const { website, ...felder } = data;
      await addDoc(collection(db, "ferienlager_registrations"), {
        ...felder, lagerId,
        depositPaid: false, depositPaidDate: "", remainderPaid: false, remainderPaidDate: "",
        funding: "", illnesses: "", adminNotes: "", confirmed: false, group: "", miscAdmin: "",
        submittedAt: serverTimestamp(),
      });
      setGesendet(true);
    } catch (err) {
      setFehler("Die Anmeldung konnte nicht gespeichert werden. Bitte versucht es erneut oder meldet euch bei der Orga. (" + err.message + ")");
    } finally {
      setWirdGesendet(false);
    }
  };

  if (lagerId === undefined) {
    return <div style={s.page}><p style={{ textAlign: "center", color: "#999" }}>Lädt …</p></div>;
  }

  if (lagerId === null) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={{ textAlign: "center", color: "#666" }}>
            Aktuell ist keine Anmeldung möglich. Bitte wendet euch an die Orga oder versucht es später erneut.
          </p>
        </div>
      </div>
    );
  }

  if (gesendet) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={{ fontSize: "2.6rem", textAlign: "center", marginBottom: "0.6rem" }}>🎉</div>
          <h2 style={{ textAlign: "center", color: "#1a3558" }}>Danke für die Anmeldung!</h2>
          <p style={{ textAlign: "center", color: "#666" }}>
            Wir haben die Anmeldung erhalten und melden uns bei Rückfragen. Weitere Infos (Anzahlung,
            Bestätigung) erhaltet ihr per E-Mail bzw. von der Orga.
          </p>
          <button style={{ ...s.btn, display: "block", margin: "1rem auto 0" }} onClick={() => { setData(leer); setGesendet(false); }}>
            Weiteres Kind anmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <div style={{ fontSize: "2.2rem" }}>🏕️🌞🎉</div>
        <h1 style={{ color: "#1a3558", marginBottom: "0.2rem" }}>Anmeldung Ferienlager Vorhelm{lagerName ? ` ${lagerName}` : ""}</h1>
        <p style={{ color: "#666" }}>Bitte füllt das Formular für jedes Kind einzeln aus. Wir freuen uns auf euch!</p>
      </div>

      <form style={s.card} onSubmit={absenden}>
        {fehler && <div style={s.fehler}>{fehler}</div>}

        <div style={s.gruppeTitle}>Angaben zum Kind</div>
        <div style={s.row}>
          <Feld label="Vorname des Kindes *" value={data.childFirstName} onChange={set("childFirstName")} />
          <Feld label="Nachname des Kindes *" value={data.childLastName} onChange={set("childLastName")} />
        </div>
        <div style={s.row}>
          <div style={s.feld}>
            <label style={s.label}>Geschlecht des Kindes *</label>
            <select style={s.input} value={data.childGender} onChange={set("childGender")}>
              <option value="">Bitte wählen …</option>
              <option value="Junge">Junge</option>
              <option value="Mädchen">Mädchen</option>
              <option value="Divers">Divers</option>
            </select>
          </div>
          <Feld label="Geburtsdatum des Kindes *" type="date" value={data.childBirthdate} onChange={set("childBirthdate")} />
          <div style={s.feld}>
            <label style={s.label}>T-Shirt-Größe (Kind)</label>
            <select style={s.input} value={data.tshirtSize} onChange={set("tshirtSize")}>
              <option value="">Bitte wählen …</option>
              {["116", "128", "140", "152", "164", "176", "S", "M", "L", "XL"].map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
        </div>

        <div style={s.gruppeTitle}>Kontaktdaten</div>
        <Feld label="Name der Erziehungsberechtigten *" value={data.guardianName} onChange={set("guardianName")} placeholder="Es können auch mehrere Personen aufgeführt werden" wide />
        <div style={s.row}>
          <Feld label="Straße *" value={data.street} onChange={set("street")} flex={2} />
          <Feld label="PLZ *" value={data.zip} onChange={set("zip")} />
          <Feld label="Ort *" value={data.city} onChange={set("city")} flex={1.5} />
        </div>
        <div style={s.row}>
          <Feld label="Telefonnummer für Rückfragen *" type="tel" value={data.phone} onChange={set("phone")} />
          <Feld label="E-Mail-Adresse (Kontaktperson) *" type="email" value={data.email} onChange={set("email")} />
        </div>

        <div style={s.gruppeTitle}>Sonstiges</div>
        <div style={s.feld}>
          <label style={s.label}>Gruppenwünsche</label>
          <textarea style={{ ...s.input, minHeight: "60px" }} value={data.groupWishes} onChange={set("groupWishes")} placeholder="Freund:innen, Geschwister, Gruppenleitung, Wunschgruppe …" />
        </div>
        <div style={s.feld}>
          <label style={s.label}>Sonstiges</label>
          <textarea style={{ ...s.input, minHeight: "60px" }} value={data.notes} onChange={set("notes")} placeholder="Weitere Hinweise für die Anmeldung" />
        </div>

        {/* Honeypot: für Menschen unsichtbar, hält einfache Spam-Bots ab */}
        <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
          <label htmlFor="website">Bitte leer lassen</label>
          <input id="website" tabIndex={-1} autoComplete="off" value={data.website} onChange={set("website")} />
        </div>

        <button type="submit" style={{ ...s.btn, width: "100%", marginTop: "0.6rem" }} disabled={wirdGesendet}>
          {wirdGesendet ? "Wird gesendet …" : "Anmeldung absenden"}
        </button>
      </form>

      <p style={{ textAlign: "center", color: "#999", fontSize: "0.75rem", marginTop: "1.2rem" }}>
        Ferienlager Vorhelm · Fragen an die Orga bitte per E-Mail oder Telefon
      </p>
    </div>
  );
}

function Feld({ label, value, onChange, type = "text", placeholder, wide, flex }) {
  return (
    <div style={{ ...s.feld, flex: flex || (wide ? "1 1 100%" : 1) }}>
      <label style={s.label}>{label}</label>
      <input style={s.input} type={type} value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

const s = {
  page: { maxWidth: "640px", margin: "0 auto", padding: "2rem 1rem 3rem", fontFamily: "sans-serif", backgroundColor: "#f0f2f5", minHeight: "100vh" },
  card: { backgroundColor: "white", borderRadius: "10px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)", padding: "1.5rem" },
  row: { display: "flex", gap: "0.7rem", flexWrap: "wrap" },
  feld: { marginBottom: "0.9rem", minWidth: "140px" },
  label: { display: "block", fontSize: "0.78rem", fontWeight: "bold", color: "#333", marginBottom: "0.25rem" },
  input: { width: "100%", padding: "0.55rem 0.6rem", border: "1.5px solid #ddd", borderRadius: "6px", fontSize: "0.9rem", fontFamily: "inherit", boxSizing: "border-box" },
  gruppeTitle: { fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", color: "#2f7d54", margin: "1rem 0 0.5rem" },
  btn: { padding: "0.7rem 1.4rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "0.95rem", fontWeight: 600 },
  fehler: { backgroundColor: "#fdecea", color: "#c0392b", padding: "0.6rem 0.8rem", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "1rem" },
};
